import { ReactNode } from "react";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { tableStyle, thStyle, tdStyle } from "../../../styles";
import { SortState } from "../../../hooks/useAdminSort";
import SortableTh from "./SortableTh";

export interface AdminTableColumn<T> {
  key: string;
  label: string;
  render: (row: T) => ReactNode;
  align?: "left" | "center" | "right";
  /** Backend sortBy value this column corresponds to. Omit for non-sortable columns (e.g. actions). */
  sortKey?: string;
}

interface Props<T> {
  columns: AdminTableColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  /** Only needed when at least one column declares a sortKey. */
  sort?: SortState;
  onSort?: (key: string) => void;
}

/**
 * The tableStyle/thStyle/tdStyle pattern repeated across admin components, with the
 * mobile overflowX wrapper applied once here instead of per call site (or, in most
 * cases, not at all — several of the tables this replaces had no mobile handling).
 */
export default function AdminTable<T>({ columns, rows, rowKey, sort, onSort }: Props<T>) {
  const isMobile = useIsMobile();

  return (
    <div style={isMobile ? { overflowX: "auto" } : undefined}>
      <table style={tableStyle}>
        <thead>
          <tr>
            {columns.map((col) => (
              col.sortKey && onSort ? (
                <SortableTh
                  key={col.key}
                  label={col.label}
                  sortKey={col.sortKey}
                  activeSortBy={sort?.sortBy ?? null}
                  sortDir={sort?.sortDir ?? "asc"}
                  onSort={onSort}
                  align={col.align}
                />
              ) : (
                <th key={col.key} style={{ ...thStyle, textAlign: col.align ?? "left" }}>{col.label}</th>
              )
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={rowKey(row)}>
              {columns.map((col) => (
                <td key={col.key} style={{ ...tdStyle, textAlign: col.align ?? "left" }}>{col.render(row)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
